import { isEmpty } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { Breadcrumb } from 'components/breadcrumb'
import { parseDateFromLocalStorage } from 'components/form'
import { PageLoading } from 'components/loading'
import { PecSwitch, PrivateRoute } from 'components/route'
import { useFlags } from 'config/useFlagsContext'
import {
  useAgendamentosDiaQuery,
  useAtendimentoVacinacaoRetificacaoViewQuery,
  useAtendimentoVacinacaoViewQuery,
} from 'graphql/hooks.generated'
import { PeriodoGestacao, SituacaoAgendadoEnum } from 'graphql/types.generated'
import { useLocalStorageState } from 'hooks/useStorage'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useRouteMatch } from 'react-router'
import { Redirect, Route } from 'react-router-dom'
import { emptyArrayOf } from 'util/array'
import { calculateAge } from 'util/date/calculateAge'
import { useRetificacaoStorage } from 'view/retificacao-atendimento/useJustificativaRetificacaoStorage'
import { initialValueRetificacaoVacinacao } from 'view/retificacao-atendimento/util-retificacaoAtendimentoVacinacao'

import Permissions from '../../../types/Permissions'
import { AgendamentosCidadaoAtendimentoView } from '../detail/agendamentos/AgendamentosCidadaoAtendimentoView'
import { CidadaoDetailView } from '../detail/cadastro-cidadao/CidadaoDetailView'
import { FolhaRostoView } from '../detail/folha-rosto/FolhaRostoView'
import { AtendimentoHeader } from '../detail/header/AtendimentoHeader'
import { ProntuarioFamiliarView } from '../detail/prontuario-familiar/ProntuarioFamiliarView'
import { SistemasExternosAtendimentoView } from '../detail/sistemas-externos/SistemasExternosAtendimentoView'
import { usePeriodoGestacional } from '../detail/soap/aside/pre-natal/hooks/usePeriodoGestacional'
import { grupoCboAcompanhamentoPreNatal } from '../detail/soap/pre-natal/acessos'
import { VacinacaoView } from '../detail/vacinacao/VacinacaoView'
import { CidadaoAtendimento } from '../types/CidadaoAtendimento'
import { AtendimentoVacinacaoState, initialValue } from './model'

interface AtendimentoVacinacaoViewProps {
  atendimentoProfissionalId: string
  isRetificacao?: boolean
}

const SITUACOES_AGENDAMENTOS = [SituacaoAgendadoEnum.AGENDADO, SituacaoAgendadoEnum.CIDADAO_PRESENTE_NA_UNIDADE]

export function AtendimentoVacinacaoView(props: Readonly<AtendimentoVacinacaoViewProps>) {
  const { atendimentoProfissionalId, isRetificacao = false } = props

  const { acesso, tipoEstabelecimento, hasCboAuth, hasAuthorization } = useAcessoLotacaoOrEstagio()
  const { PILOTO_PRONTUARIO_FAMILIAR_ENABLED } = useFlags()

  const {
    data: { atendimentoIndividual: atendimentoProfissional },
    loading: loadingAtendimento,
  } = useAtendimentoVacinacaoViewQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      atendimentoProfissionalId,
    },
  })

  const {
    atendimento: {
      id: atendimentoId = null,
      agendado = null,
      tiposServico = null,
      prontuario = null,
      isRegistroTardio = null,
      cidadao = null,
      statusRevisaoAtendimento = null,
    } = {},
  } = atendimentoProfissional || {}

  const { getRetificacaoStorageValue } = useRetificacaoStorage(atendimentoId)

  const match = useRouteMatch()

  const [cacheState, setCacheState, deleteCacheState] = useLocalStorageState<AtendimentoVacinacaoState>(
    `${acesso?.id}/atendimento/vacinacao/${atendimentoId}`,
    { modifier: cacheParser, checkVersionCompatibility: true }
  )

  const {
    data: atendimentoVacinacaoRetificacao,
    loading: loadingRetificacaoVacinacao,
  } = useAtendimentoVacinacaoRetificacaoViewQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isRetificacao,
    variables: {
      atendimentoProfissionalId,
    },
  })

  const dataAtendimento = atendimentoProfissional?.iniciadoEm

  const canAccess = hasAuthorization(Permissions.visualizarListaDeAtendimento.cadastrarEditarEExcluir)
  const canAccessAgenda = hasAuthorization(Permissions.visualizarAgenda.agendarEEditar)

  const [initialStateLoaded, setInitialStateLoaded] = useState(!!cacheState)

  const idadeEmAnos = useMemo(
    () => cidadao?.dataNascimento && calculateAge(cidadao.dataNascimento, atendimentoProfissional?.iniciadoEm).years,
    [atendimentoProfissional, cidadao]
  )

  const cidadaoComIdadeEmAnos: CidadaoAtendimento = { ...cidadao, idadeEmAnos }

  const today = moment().startOf('day').toDate()

  const { data: agendamentosDiaQueryResults, loading: loadingAgendamento } = useAgendamentosDiaQuery({
    variables: {
      input: {
        cidadaoId: cidadao?.id,
        dataAgendadoInicio: today,
        dataAgendadoFim: today,
        situacao: SITUACOES_AGENDAMENTOS,
        unidadeSaudeId: acesso?.unidadeSaude.id,
        isForaDaUbs: false,
        isOrigemAtencaoDomiciliar: false,
      },
    },
    skip: !cidadao?.id,
  })

  const agendamentosDia = agendamentosDiaQueryResults?.agendados?.content?.filter(
    (agendadoItem) => agendadoItem.id !== agendado?.id || agendadoItem?.lotacaoAgendada?.id !== acesso?.id
  )

  const isLoading = loadingAtendimento || loadingAgendamento || loadingRetificacaoVacinacao
  const isGestante = !isEmpty(prontuario?.preNatalAtivo)
  const rascunhoAtendimento = atendimentoProfissional?.rascunho

  useEffect(() => {
    if (!isLoading) {
      const agendadoSelecionado = cacheState?.finalizacaoVacinacao.desfechoAtendimento?.atendimento?.agendado
      if (agendadoSelecionado && !agendamentosDia?.find((agendamentos) => agendamentos.id === agendadoSelecionado)) {
        cacheState.finalizacaoVacinacao.desfechoAtendimento.atendimento.agendado = undefined
      }

      if (!initialStateLoaded) {
        if (rascunhoAtendimento) {
          setCacheState(cacheParser(JSON.parse(rascunhoAtendimento) as AtendimentoVacinacaoState))
        } else if (isRetificacao) {
          setCacheState(
            initialValueRetificacaoVacinacao(
              atendimentoVacinacaoRetificacao?.atendimentoIndividual,
              getRetificacaoStorageValue()
            )
          )
        } else {
          setCacheState(initialValue(prontuario?.puerpera, isGestante, tiposServico, canAccess, agendamentosDia))
        }
        setInitialStateLoaded(true)
      }
    }
  }, [
    agendamentosDia,
    cacheState,
    canAccess,
    initialStateLoaded,
    isGestante,
    isLoading,
    prontuario,
    rascunhoAtendimento,
    setCacheState,
    isRetificacao,
    atendimentoVacinacaoRetificacao,
    getRetificacaoStorageValue,
    tiposServico,
  ])

  const prontuarioId = prontuario?.id
  const preNatalAtivo = prontuario?.preNatalAtivo
  const ultimaDumPreNatalAtivo = preNatalAtivo?.ultimaDum

  const { loading: loadingDataPeriodoGestacional, idadeGestacional } = usePeriodoGestacional({
    prontuarioId: prontuarioId,
    dum: ultimaDumPreNatalAtivo,
    dataAtendimento,
    skip: !hasCboAuth(grupoCboAcompanhamentoPreNatal),
  })

  if (isLoading || !initialStateLoaded || loadingDataPeriodoGestacional) {
    return (
      <>
        <Breadcrumb title='Atendimento de vacinação' />
        <PageLoading message='Carregando dados do atendimento de vacinação...' />
      </>
    )
  }

  const renderFolhaRostoView = () => (
    <FolhaRostoView
      isRegistroTardio={isRegistroTardio}
      cidadao={cidadaoComIdadeEmAnos}
      lotacao={atendimentoProfissional?.lotacao}
      prontuarioId={prontuarioId}
      dataReferencia={atendimentoProfissional?.iniciadoEm}
      isGestante={isGestante}
      gestacoes={prontuario?.gestacoes ?? emptyArrayOf<PeriodoGestacao>()}
      isAtendimentoAgendado={!!agendado}
      atendimentoId={atendimentoId}
      tipoAtendimentoProfissional={atendimentoProfissional?.tipo}
      clearCache={deleteCacheState}
      idadeGestacional={idadeGestacional}
      isAtendimentoOdonto={false}
      isAtendimentoProcedimentos={false}
    />
  )

  const renderVacinacaoView = () => {
    return (
      <VacinacaoView
        cacheState={cacheState}
        atendimentoId={atendimentoId}
        prontuarioId={prontuarioId}
        cidadao={cidadaoComIdadeEmAnos}
        dataAtendimento={atendimentoProfissional?.iniciadoEm}
        clearCache={deleteCacheState}
        updateCache={setCacheState}
        agendamentosDia={agendamentosDia}
        gestante={isGestante}
        gestacoes={prontuario?.gestacoes || []}
        puerpera={prontuario?.puerpera}
        tipoEstabelecimento={tipoEstabelecimento}
        lastSaved={cacheState?.lastSaved}
        canAccessAgenda={canAccessAgenda}
        statusRevisaoAtendimento={statusRevisaoAtendimento}
        tipoAtendimentoProfissional={atendimentoProfissional?.tipo}
        atendimentoProfissionalId={atendimentoProfissional?.id}
        isRetificacao={isRetificacao}
      />
    )
  }

  const renderCidadaoDetail = () => (
    <CidadaoDetailView
      atendimentoId={atendimentoId}
      cidadaoId={cidadao?.id}
      clearCache={deleteCacheState}
      tipoAtendimentoProfissional={atendimentoProfissional?.tipo}
      callbackUrl='lista-atendimento/atendimento-vacinacao'
    />
  )

  const renderAgendamentos = () => (
    <AgendamentosCidadaoAtendimentoView
      atendimentoId={atendimentoId}
      tipoAtendimentoProfissional={atendimentoProfissional.tipo}
      isRegistroTardio={isRegistroTardio}
      clearCache={deleteCacheState}
    />
  )

  const renderSistemasExternos = () => (
    <SistemasExternosAtendimentoView
      atendimentoId={atendimentoId}
      cidadaoId={cidadao?.id}
      clearCache={deleteCacheState}
      isRegistroTardio={isRegistroTardio}
    />
  )

  const renderProntuarioFamiliar = () => <ProntuarioFamiliarView cidadaoId={cidadao?.id} />

  return (
    <>
      <Breadcrumb title='Atendimento de vacinação' />
      <AtendimentoHeader
        cidadaoId={cidadao?.id}
        renderFolhaRosto={false}
        renderSoap={false}
        renderHistorico={false}
        renderVacinacao
        cnsProfissional={atendimentoProfissional.lotacao.profissional.cns}
        cnsCidadao={cidadao?.cns}
        cnes={atendimentoProfissional.lotacao.unidadeSaude.cnes}
        municipioId={atendimentoProfissional.lotacao.municipio.id}
        dataAtendimento={atendimentoProfissional?.iniciadoEm}
        isRetificacao={isRetificacao}
        tipoAtendimentoProfissional={atendimentoProfissional.tipo}
        renderProntuarioFamiliar={PILOTO_PRONTUARIO_FAMILIAR_ENABLED}
      />
      <PecSwitch>
        <Redirect exact path={match.url} to={`${match.url}/vacinacao`} />
        <Route path={`${match.url}/vacinacao`} render={renderVacinacaoView} />
        {!isRetificacao && (
          <>
            <PrivateRoute
              path={`${match.url}/folha-rosto`}
              render={renderFolhaRostoView}
              permission={Permissions.visualizarProntuario}
            />
            <Route path={`${match.url}/cadastro-cidadao`} render={renderCidadaoDetail} />
            <Route path={`${match.path}/agendamentos/:cidadaoId`} render={renderAgendamentos} />
            <Route path={`${match.path}/sistemas-externos/:iframeId?`} render={renderSistemasExternos} />
            {PILOTO_PRONTUARIO_FAMILIAR_ENABLED && (
              <PrivateRoute
                path={`${match.path}/prontuario-familiar`}
                render={renderProntuarioFamiliar}
                permission={Permissions.gestaoDeCadastrosIndividuais.visualizarCadastroIndividual}
              />
            )}
          </>
        )}
      </PecSwitch>
    </>
  )
}

const cacheParser = (cacheState: AtendimentoVacinacaoState): AtendimentoVacinacaoState =>
  cacheState
    ? {
        ...cacheState,
        finalizacaoVacinacao: {
          ...cacheState.finalizacaoVacinacao,
          agendamentoConsultas: cacheState.finalizacaoVacinacao?.agendamentoConsultas
            ? {
                ...cacheState.finalizacaoVacinacao.agendamentoConsultas,
                avulsa: cacheState.finalizacaoVacinacao.agendamentoConsultas.avulsa
                  ? {
                      ...cacheState.finalizacaoVacinacao.agendamentoConsultas.avulsa,
                      horario: parseDateFromLocalStorage(
                        cacheState.finalizacaoVacinacao.agendamentoConsultas.avulsa.horario
                      ),
                    }
                  : undefined,
              }
            : undefined,
        },
      }
    : undefined
